/**
 * API Client Functions for LLM Providers
 * Handles communication with OpenAI, Anthropic, Groq, OpenRouter, and custom endpoints
 */

import { Logger } from '../logger';

const log = Logger.create('apiClients');

/**
 * Convert file to base64 string
 */
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:mime;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Cache for OpenRouter model capabilities
let openRouterModelsCache = null;
let openRouterModelsCacheTime = 0;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour cache

/**
 * Fetch OpenRouter models and their capabilities
 * @returns {Promise<Map<string, object>>} Map of model ID to model info
 */
async function fetchOpenRouterModels() {
  const now = Date.now();
  
  // Return cached data if still valid
  if (openRouterModelsCache && (now - openRouterModelsCacheTime) < CACHE_DURATION) {
    return openRouterModelsCache;
  }
  
  try {
    const response = await fetch('https://openrouter.ai/api/v1/models');
    if (!response.ok) {
      log.warn('Failed to fetch OpenRouter models:', response.statusText);
      return new Map();
    }
    
    const data = await response.json();
    const modelsMap = new Map();
    
    if (data.data) {
      for (const model of data.data) {
        modelsMap.set(model.id, model);
      }
    }
    
    openRouterModelsCache = modelsMap;
    openRouterModelsCacheTime = now;
    return modelsMap;
  } catch (error) {
    log.warn('Error fetching OpenRouter models:', error);
    return new Map();
  }
}

/**
 * Check if an OpenRouter model has native file support
 * @param {string} modelId - The model ID (e.g., "openai/gpt-4o")
 * @returns {Promise<boolean>} Whether the model supports native file input
 */
export async function hasNativeFileSupport(modelId) {
  const models = await fetchOpenRouterModels();
  const model = models.get(modelId);
  
  if (model && model.architecture && model.architecture.input_modalities) {
    return model.architecture.input_modalities.includes('file');
  }
  
  // Fallback to model name detection if API fails
  const modelLower = modelId.toLowerCase();
  return (
    modelLower.includes('gpt-4o') ||
    modelLower.includes('gpt-4-turbo') ||
    modelLower.includes('gpt-5') ||
    modelLower.includes('claude') ||
    modelLower.includes('gemini')
  );
}

/**
 * Get model info from OpenRouter
 * @param {string} modelId - The model ID
 * @returns {Promise<object|null>} Model info or null if not found
 */
export async function getOpenRouterModelInfo(modelId) {
  const models = await fetchOpenRouterModels();
  return models.get(modelId) || null;
}

/**
 * Call OpenAI-compatible API with full message array (for conversation context)
 * Works for OpenAI, Groq, OpenRouter
 * @param {Array} messages - Array of { role: 'system'|'user'|'assistant', content: string }
 * @param {string} apiKey - API key
 * @param {string} model - Model name
 * @param {string} baseUrl - Base URL (optional)
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 */
export async function callOpenAIWithMessages(messages, apiKey, model, baseUrl, temperature, maxTokens) {
  const defaultEndpoint = 'https://api.openai.com/v1/chat/completions';
  const endpoint = baseUrl || defaultEndpoint;
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }
  
  try {
    const requestBody = {
      model: model,
      messages: messages,
      temperature: temperature,
      max_tokens: maxTokens
    };
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorMsg = response.statusText;
      try {
        const error = await response.json();
        errorMsg = error.error?.message || errorMsg;
      } catch (e) {
        // If response is not JSON, use statusText
      }
      throw new Error(`API Fehler (${response.status}): ${errorMsg}`);
    }

    const data = await response.json();
    
    if (data.choices && data.choices[0]) {
      return data.choices[0].message.content;
    }
    
    throw new Error('Unexpected API response format');
  } catch (fetchError) {
    if (fetchError.message.includes('Failed to fetch') || fetchError.name === 'TypeError') {
      throw new Error(`Verbindungsfehler: Kann ${endpoint} nicht erreichen.`);
    }
    throw fetchError;
  }
}

/**
 * Call Anthropic API with full message array (for conversation context)
 * @param {string} systemPrompt - System prompt (separate in Anthropic)
 * @param {Array} messages - Array of { role: 'user'|'assistant', content: string }
 * @param {string} apiKey - API key
 * @param {string} model - Model name
 * @param {string} baseUrl - Base URL (optional)
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 */
export async function callAnthropicWithMessages(systemPrompt, messages, apiKey, model, baseUrl, temperature, maxTokens) {
  const endpoint = baseUrl || 'https://api.anthropic.com/v1/messages';
  
  // Filter out system messages from the array (Anthropic uses separate system field)
  const chatMessages = messages.filter(m => m.role !== 'system');
  
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model: model,
      max_tokens: maxTokens,
      system: systemPrompt,
      temperature: temperature,
      messages: chatMessages
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Anthropic API Fehler: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  return data.content[0].text;
}

/**
 * Call OpenAI-compatible API (works for OpenAI, Groq, OpenRouter)
 * @param {Object} options - Configuration object
 * @param {string} options.systemPrompt - System prompt
 * @param {string} options.userPrompt - User prompt
 * @param {string} options.apiKey - API key
 * @param {string} options.model - Model name
 * @param {string} options.baseUrl - Base URL (optional)
 * @param {number} options.temperature - Temperature
 * @param {number} options.maxTokens - Max tokens
 * @param {Array} options.files - Files to upload (optional)
 * @param {boolean} options.useNativeUpload - Whether to use native file upload
 */
export async function callOpenAI(systemPrompt, userPrompt, apiKey, model, baseUrl, temperature, maxTokens, files = [], useNativeUpload = false) {
  // Determine if this is OpenRouter
  const isOpenRouter = baseUrl && baseUrl.includes('openrouter.ai');
  
  // Use Responses API for OpenAI file uploads, Chat Completions for text-only or OpenRouter
  const hasFiles = useNativeUpload && files.length > 0;
  const useResponsesAPI = hasFiles && !isOpenRouter;
  
  const defaultEndpoint = useResponsesAPI 
    ? 'https://api.openai.com/v1/responses'
    : 'https://api.openai.com/v1/chat/completions';
  const endpoint = baseUrl || defaultEndpoint;
  
  // Build headers - only add Authorization if apiKey is provided
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }
  
  try {
    let requestBody;
    
    // OpenRouter uses different format for files
    if (hasFiles && isOpenRouter) {
      // Build user message with text and files (OpenRouter format)
      const userContentParts = [
        { type: 'text', text: userPrompt }
      ];
      
      // Add PDF files as base64 (OpenRouter format)
      for (const file of files) {
        if (file.name.toLowerCase().endsWith('.pdf')) {
          const base64Data = await fileToBase64(file);
          userContentParts.push({
            type: 'file',
            file: {
              filename: file.name,
              file_data: `data:application/pdf;base64,${base64Data}`
            }
          });
        }
      }
      
      // Check if model has native file support via OpenRouter API
      // This queries the API to see if 'file' is in input_modalities
      const modelHasNativeSupport = await hasNativeFileSupport(model);
      
      // OpenRouter Chat Completions format with files
      requestBody = {
        model: model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userContentParts }
        ],
        temperature: temperature,
        max_tokens: maxTokens,
        // Configure PDF processing engine
        // - 'native': For models with direct file support (detected via API)
        // - 'pdf-text': Free text extraction for other models
        plugins: [
          {
            id: 'file-parser',
            pdf: {
              engine: modelHasNativeSupport ? 'native' : 'pdf-text'
            }
          }
        ]
      };
    }
    // OpenAI Responses API format
    else if (hasFiles) {
      // Build user message with text and files (OpenAI Responses API format)
      const userContentParts = [{ type: 'input_text', text: userPrompt }];
      
      // Add PDF files as base64
      for (const file of files) {
        if (file.name.toLowerCase().endsWith('.pdf')) {
          const base64Data = await fileToBase64(file);
          userContentParts.push({
            type: 'input_file',
            filename: file.name,
            file_data: base64Data
          });
        }
      }
      
      // Responses API format - input is array of messages
      requestBody = {
        model: model,
        input: [{
          role: 'user',
          content: userContentParts
        }],
        instructions: systemPrompt,  // System prompt goes in instructions field
        temperature: temperature,
        max_output_tokens: maxTokens
      };
    } else {
      // Chat Completions API format (text-only)
      requestBody = {
        model: model,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: temperature,
        max_tokens: maxTokens
      };
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorMsg = response.statusText;
      try {
        const error = await response.json();
        errorMsg = error.error?.message || errorMsg;
      } catch (e) {
        // If response is not JSON, use statusText
      }
      throw new Error(`API Fehler (${response.status}): ${errorMsg}`);
    }

    const data = await response.json();
    
    // Handle Responses API format (used for file uploads)
    if (data.output) {
      // Find the assistant message in output array
      const assistantMessage = data.output.find(item => 
        item.type === 'message' && item.role === 'assistant'
      );
      
      if (assistantMessage && assistantMessage.content) {
        // Extract text from content array
        const textContent = assistantMessage.content.find(c => c.type === 'output_text');
        return textContent ? textContent.text : '';
      }
    }
    
    // Handle Chat Completions API format (text-only)
    if (data.choices && data.choices[0]) {
      return data.choices[0].message.content;
    }
    
    throw new Error('Unexpected API response format');
  } catch (fetchError) {
    if (fetchError.message.includes('Failed to fetch') || fetchError.name === 'TypeError') {
      throw new Error(`Verbindungsfehler: Kann ${endpoint} nicht erreichen. Stelle sicher, dass:\n- Der Service läuft\n- Die URL korrekt ist\n- Kein Ad-Blocker die Anfrage blockiert`);
    }
    throw fetchError;
  }
}

/**
 * Call Anthropic API (Claude)
 * @param {Object} options - Configuration object
 * @param {string} options.systemPrompt - System prompt
 * @param {string} options.userPrompt - User prompt
 * @param {string} options.apiKey - API key
 * @param {string} options.model - Model name
 * @param {string} options.baseUrl - Base URL (optional)
 * @param {number} options.temperature - Temperature
 * @param {number} options.maxTokens - Max tokens
 * @param {Array} options.files - Files to upload (optional)
 * @param {boolean} options.useNativeUpload - Whether to use native file upload
 */
export async function callAnthropic(systemPrompt, userPrompt, apiKey, model, baseUrl, temperature, maxTokens, files = [], useNativeUpload = false) {
  const endpoint = baseUrl || 'https://api.anthropic.com/v1/messages';
  
  // Build message content
  let userContent;
  
  // If native upload is enabled and we have PDF files
  if (useNativeUpload && files.length > 0) {
    const contentParts = [{ type: 'text', text: userPrompt }];
    
    // Add PDF files as base64
    for (const file of files) {
      if (file.name.toLowerCase().endsWith('.pdf')) {
        const base64Data = await fileToBase64(file);
        contentParts.push({
          type: 'document',
          source: {
            type: 'base64',
            media_type: 'application/pdf',
            data: base64Data
          }
        });
      }
    }
    
    userContent = contentParts;
  } else {
    userContent = userPrompt;
  }
  
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model: model,
      max_tokens: maxTokens,
      system: systemPrompt,
      temperature: temperature,
      messages: [
        { role: 'user', content: userContent }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Anthropic API Fehler: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  return data.content[0].text;
}

/**
 * Call custom endpoint
 */
export async function callCustomEndpoint(systemPrompt, userPrompt, endpoint, apiKey, model, temperature, maxTokens) {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }

  const requestBody = {
    system: systemPrompt,
    prompt: userPrompt,
    model: model,
    temperature: temperature,
    max_tokens: maxTokens
  };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    throw new Error(`Custom API Fehler: ${response.statusText}`);
  }

  const data = await response.json();
  return data.response || data.content || data.text || data.choices?.[0]?.message?.content || JSON.stringify(data);
}

/**
 * Call OpenAI-compatible API with images (for vision models)
 * Works for OpenAI GPT-4o, GPT-4-turbo, OpenRouter vision models
 * @param {string} systemPrompt - System prompt
 * @param {string} userPrompt - User prompt
 * @param {Array} images - Array of { base64: string, mimeType: string, pageNumber?: number }
 * @param {string} apiKey - API key
 * @param {string} model - Model name
 * @param {string} baseUrl - Base URL (optional)
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 */
/**
 * Call OpenAI-compatible API with images (Vision API)
 * @param {string} systemPrompt - System prompt
 * @param {string} userPrompt - User prompt  
 * @param {Array} images - Array of { base64: string, mimeType: string }
 * @param {string} apiKey - API key
 * @param {string} model - Model name
 * @param {string} baseUrl - Base URL (optional)
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 * @param {string} imageDetail - Image detail level: 'low', 'high', or 'auto' (default: 'auto')
 */
export async function callOpenAIWithImages(systemPrompt, userPrompt, images, apiKey, model, baseUrl, temperature, maxTokens, imageDetail = 'auto') {
  const endpoint = baseUrl || 'https://api.openai.com/v1/chat/completions';
  
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`;
  }
  
  // Map imageQuality to OpenAI detail parameter
  // 'low' = faster, cheaper, 512px max
  // 'high' = slower, more expensive, full resolution
  // 'auto' = let API decide
  const detail = imageDetail === 'low' ? 'low' : imageDetail === 'high' ? 'high' : 'auto';
  
  // Build multimodal content array
  const contentParts = [
    { type: 'text', text: userPrompt }
  ];
  
  // Add images with the specified detail level
  for (const img of images) {
    contentParts.push({
      type: 'image_url',
      image_url: {
        url: `data:${img.mimeType};base64,${img.base64}`,
        detail: detail
      }
    });
  }
  
  const requestBody = {
    model: model,
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: contentParts }
    ],
    temperature: temperature,
    max_tokens: maxTokens
  };
  
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorMsg = response.statusText;
      try {
        const error = await response.json();
        errorMsg = error.error?.message || errorMsg;
      } catch (e) {
        // If response is not JSON, use statusText
      }
      throw new Error(`Vision API Fehler (${response.status}): ${errorMsg}`);
    }

    const data = await response.json();
    
    if (data.choices && data.choices[0]) {
      return data.choices[0].message.content;
    }
    
    throw new Error('Unexpected Vision API response format');
  } catch (fetchError) {
    if (fetchError.message.includes('Failed to fetch') || fetchError.name === 'TypeError') {
      throw new Error(`Verbindungsfehler: Kann ${endpoint} nicht erreichen.`);
    }
    throw fetchError;
  }
}

/**
 * Call Anthropic API with images (for Claude vision models)
 * @param {string} systemPrompt - System prompt
 * @param {string} userPrompt - User prompt
 * @param {Array} images - Array of { base64: string, mimeType: string, pageNumber?: number }
 * @param {string} apiKey - API key
 * @param {string} model - Model name
 * @param {string} baseUrl - Base URL (optional)
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 */
export async function callAnthropicWithImages(systemPrompt, userPrompt, images, apiKey, model, baseUrl, temperature, maxTokens) {
  const endpoint = baseUrl || 'https://api.anthropic.com/v1/messages';
  
  // Build multimodal content array
  const contentParts = [];
  
  // Add images first (Anthropic recommends images before text)
  for (const img of images) {
    contentParts.push({
      type: 'image',
      source: {
        type: 'base64',
        media_type: img.mimeType,
        data: img.base64
      }
    });
  }
  
  // Add text after images
  contentParts.push({ type: 'text', text: userPrompt });
  
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model: model,
      max_tokens: maxTokens,
      system: systemPrompt,
      temperature: temperature,
      messages: [
        { role: 'user', content: contentParts }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Anthropic Vision API Fehler: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  return data.content[0].text;
}

/**
 * Check if model supports vision/images
 * @param {string} model - Model name
 * @param {string} provider - Provider name ('openai', 'anthropic', 'openrouter', etc.)
 * @returns {boolean} Whether the model supports images
 */
export function supportsVision(model, provider) {
  const modelLower = model.toLowerCase();
  
  // OpenAI vision models
  if (provider === 'openai') {
    return modelLower.includes('gpt-4o') || 
           modelLower.includes('gpt-4-turbo') ||
           modelLower.includes('gpt-4-vision');
  }
  
  // Anthropic vision models (Claude 3+)
  if (provider === 'anthropic') {
    return modelLower.includes('claude-3');
  }
  
  // OpenRouter - check model name patterns for vision-capable models
  if (provider === 'openrouter') {
    return modelLower.includes('gpt-4o') ||
           modelLower.includes('gpt-4-turbo') ||
           modelLower.includes('claude-3') ||
           modelLower.includes('gemini-pro-vision') ||
           modelLower.includes('gemini-1.5') ||
           modelLower.includes('gemini-2') ||
           modelLower.includes('llava') ||
           modelLower.includes('vision') ||
           modelLower.includes('pixtral');
  }
  
  // Groq - Llama 4 Scout/Maverick vision models
  if (provider === 'groq') {
    return modelLower.includes('llama-4-scout') ||
           modelLower.includes('llama-4-maverick') ||
           modelLower.includes('llama-4') ||
           modelLower.includes('vision') || 
           modelLower.includes('llava');
  }
  
  return false;
}

/**
 * Call Groq API with images (for Llama 4 Scout/Maverick vision models)
 * Uses OpenAI-compatible format
 * @param {string} systemPrompt - System prompt
 * @param {string} userPrompt - User prompt
 * @param {Array} images - Array of { base64: string, mimeType: string }
 * @param {string} apiKey - API key
 * @param {string} model - Model name (e.g., 'meta-llama/llama-4-scout-17b-16e-instruct')
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 */
export async function callGroqWithImages(systemPrompt, userPrompt, images, apiKey, model, temperature, maxTokens) {
  const endpoint = 'https://api.groq.com/openai/v1/chat/completions';
  
  // Build multimodal content array
  const contentParts = [
    { type: 'text', text: userPrompt }
  ];
  
  // Add images (Groq doesn't support detail parameter)
  for (const img of images) {
    contentParts.push({
      type: 'image_url',
      image_url: {
        url: `data:${img.mimeType};base64,${img.base64}`
      }
    });
  }
  
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: contentParts }
      ],
      temperature: temperature,
      max_tokens: maxTokens
    })
  });

  if (!response.ok) {
    let errorMsg = response.statusText;
    try {
      const error = await response.json();
      errorMsg = error.error?.message || errorMsg;
    } catch (e) {
      // If response is not JSON, use statusText
    }
    throw new Error(`Groq Vision API Fehler (${response.status}): ${errorMsg}`);
  }

  const data = await response.json();
  
  if (data.choices && data.choices[0]) {
    return data.choices[0].message.content;
  }
  
  throw new Error('Unexpected Groq Vision API response format');
}

/**
 * Call OpenRouter API with images (for vision models)
 * Uses OpenAI-compatible format
 * @param {string} systemPrompt - System prompt
 * @param {string} userPrompt - User prompt
 * @param {Array} images - Array of { base64: string, mimeType: string, pageNumber?: number }
 * @param {string} apiKey - API key
 * @param {string} model - Model name
 * @param {number} temperature - Temperature
 * @param {number} maxTokens - Max tokens
 * @param {string} imageDetail - Image detail level: 'low', 'high', or 'auto' (default: 'auto')
 */
export async function callOpenRouterWithImages(systemPrompt, userPrompt, images, apiKey, model, temperature, maxTokens, imageDetail = 'auto') {
  const endpoint = 'https://openrouter.ai/api/v1/chat/completions';
  
  // Map imageQuality to detail parameter
  const detail = imageDetail === 'low' ? 'low' : imageDetail === 'high' ? 'high' : 'auto';
  
  // Build multimodal content array
  const contentParts = [
    { type: 'text', text: userPrompt }
  ];
  
  // Add images with the specified detail level
  for (const img of images) {
    contentParts.push({
      type: 'image_url',
      image_url: {
        url: `data:${img.mimeType};base64,${img.base64}`,
        detail: detail
      }
    });
  }
  
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
      'HTTP-Referer': window.location.origin,
      'X-Title': 'Quiz LLM Generator'
    },
    body: JSON.stringify({
      model: model,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: contentParts }
      ],
      temperature: temperature,
      max_tokens: maxTokens
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`OpenRouter Vision API Fehler: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();
  
  if (data.choices && data.choices[0]) {
    return data.choices[0].message.content;
  }
  
  throw new Error('Unexpected OpenRouter Vision API response format');
}

/**
 * JSON Repair System Prompt for fixing malformed JSON
 */
const JSON_REPAIR_PROMPT = `Du bist ein JSON-Reparatur-Experte. Deine einzige Aufgabe ist es, ungültiges JSON zu reparieren.

REGELN:
1. Gib NUR das reparierte JSON zurück, keine Erklärungen
2. Behalte die ursprüngliche Struktur bei
3. Korrigiere häufige Fehler wie:
   - Fehlende oder zusätzliche Kommas
   - Nicht geschlossene Strings, Arrays oder Objekte
   - Ungültige Escape-Sequenzen
   - Trailing Kommas
   - Fehlende Anführungszeichen bei Keys
4. Wenn du Markdown-Code-Blöcke siehst, extrahiere nur das JSON
5. Falls das JSON fundamental kaputt ist, versuche die erkennbare Struktur zu rekonstruieren

Gib das reparierte JSON direkt aus, ohne \`\`\`json Marker.`;

/**
 * Attempt to repair invalid JSON using an LLM call
 * @param {string} invalidJson - The invalid JSON string to repair
 * @param {Object} options - LLM configuration
 * @returns {string} - Repaired JSON string or throws if repair fails
 */
export async function repairJsonWithLLM(invalidJson, options) {
  const { llmProvider, apiKey, model, baseUrl, temperature = 0.1 } = options;
  
  const userPrompt = `Repariere dieses ungültige JSON:\n\n${invalidJson}`;
  
  let response;
  
  try {
    if (llmProvider === 'anthropic') {
      response = await callAnthropic(
        JSON_REPAIR_PROMPT, 
        userPrompt, 
        apiKey, 
        model, 
        baseUrl, 
        temperature, 
        4096
      );
    } else {
      // Use OpenAI-compatible endpoint for all others
      response = await callOpenAI(
        JSON_REPAIR_PROMPT, 
        userPrompt, 
        apiKey, 
        model, 
        baseUrl, 
        temperature, 
        4096
      );
    }
    
    // Clean up response - remove code blocks if present
    let cleanedResponse = response.trim();
    if (cleanedResponse.startsWith('```json')) {
      cleanedResponse = cleanedResponse.slice(7);
    } else if (cleanedResponse.startsWith('```')) {
      cleanedResponse = cleanedResponse.slice(3);
    }
    if (cleanedResponse.endsWith('```')) {
      cleanedResponse = cleanedResponse.slice(0, -3);
    }
    cleanedResponse = cleanedResponse.trim();
    
    // Validate the repaired JSON
    JSON.parse(cleanedResponse);
    
    return cleanedResponse;
  } catch (error) {
    throw new Error(`JSON-Reparatur fehlgeschlagen: ${error.message}`);
  }
}
